import React from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { map, reduce, round } from "lodash";
import moment from "moment";
import Totals from "../../costing/total";
import DashboardCardField from "../../dashboard/card/DashboardCardField";
import ReportCard from "../../dashboard/report-card";
import { ClaimReal, ClaimReceipt, ClaimVariation } from "../../../models/claim";
import { calcGST, GST_PERCENT } from "../../../utils/calculations";
import CardTable from "../../dashboard/table-card/CardTable";
import {
  TableCardData,
  TableRowActionData,
} from "../../dashboard/table-card/utils";
import { formatQuoteNumber } from "../../../utils/text";
import { DATE_FORMAT_DATE_STRING } from "../../../constants/dates";

type ClientClaimCardProps = {
  claim: ClaimReal;
  onPrint?: (id: string) => void;
};

const ClientClaimCard: React.FC<ClientClaimCardProps> = ({
  claim,
  onPrint,
}) => {
  const { t } = useTranslation();
  const {
    _id,
    invNumber,
    status,
    richComment,
    claimDate,
    variations,
    receipts,
  } = claim;

  const handlePrint = React.useCallback(() => {
    onPrint && onPrint(_id);
  }, [onPrint, _id]);

  const renderTotal = React.useCallback(() => {
    let amount = 0;
    let gst = 0;
    let retention = 0;
    let retentionPercent = 0;
    let total = 0;

    if (claim.invoiceSubTotal) {
      //jobType === JobType.COST_PLUS) {
      amount = claim.invoiceSubTotal;
      gst = claim.invoiceGST;
      total = claim.invoiceTotal;
      retention = claim.retainedAmount;
      retentionPercent = round((retention / (amount + retention)) * 100, 2);
    } else {
      // manual calculation (legacy as of 02.02.23)
      amount = claim.amount;
      if (claim.retainedAmount) {
        amount += Number(claim.retainedAmount);
        retention = claim.retainedAmount;
        retentionPercent = round((retention / amount) * 100, 2);
      }

      const variationsWithoutGST = reduce(
        claim.variations,
        (total, variation) => {
          return total + (!variation.GST ? variation.subTotal : 0);
        },
        0
      );

      gst = calcGST(amount - variationsWithoutGST - retention, GST_PERCENT);
      total = amount - retention + gst;
    }

    return (
      <Totals
        title={t("claims.amount")}
        gst={gst}
        total={total}
        subtotal={amount}
        retention={retention}
        retentionPercent={retentionPercent}
      />
    );
  }, [claim, t]);

  const claimTable = React.useMemo<TableCardData<ClaimReal>>(() => {
    return {
      columns: [
        {
          valueKey: "description",
          title: t("claims.description"),
        },
        {
          valueKey: "amount",
          title: t("costing.totalEx"),
          className: "text-right",
          formatValue: (row: any, column: any, value: number) =>
            t("common.currency", { amount: value }),
        },
      ],
      rows: [
        {
          cells: {
            ...claim,
            amount:
              Number(claim.amount || 0) - Number(claim.variationAmount || 0),
          },
        },
      ],
    };
  }, [claim]);

  const claimVariationsTable = React.useMemo<
    TableCardData<ClaimVariation>
  >(() => {
    return {
      columns: [
        {
          valueKey: "variationNumber",
          title: t("variations.variationNumber"),
          formatValue: (row: any, column: any, value: number) =>
            formatQuoteNumber(value, "V"),
        },
        {
          valueKey: "date",
          title: t("claims.date"),
          formatValue: (row: any, column: any, value: string) =>
            moment(value).format("Do MMMM YYYY"),
        },
        {
          valueKey: "name",
          title: t("common.name"),
          formatValue: (row: any, column: any, value: string) => value,
        },
        {
          valueKey: "subTotal",
          title: t("costing.totalEx"),
          className: "text-right",
          formatValue: (row: any, column: any, value: number) =>
            t("common.currency", { amount: value }),
        },
      ],
      rows: map(variations, (variation) => ({
        cells: variation,
      })),
    };
  }, [t, variations]);

  const hasReceipt = React.useMemo(() => {
    return receipts && receipts.length > 0;
  }, [receipts]);

  const receiptsData = React.useMemo<TableCardData<ClaimReceipt>>(
    () => ({
      columns: [
        {
          valueKey: "dateReceived",
          title: t("claims.receivedDate"),
          formatValue: (row: any, column: any, value: string) =>
            moment(value).format(DATE_FORMAT_DATE_STRING),
        },
        {
          valueKey: "reference",
          title: t("common.reference"),
        },
        {
          valueKey: "amount",
          title: t("claims.amount"),
          formatValue: (row: any, column: any, value: string) =>
            t("common.currency", { amount: value }),
        },
      ],
      rows: map(receipts, (receipt) => ({
        cells: receipt,
      })),
    }),
    [receipts]
  );

  const receiptsRowActions: TableRowActionData<ClaimReceipt>[] = React.useMemo(
    () => [
      {
        icon: "comment",
        dropdownId: "view",
        tooltip: (row) => row.note,
        shouldRender: (row) => !!row.note,
      },
    ],
    []
  );

  return (
    <ReportCard
      onPrint={handlePrint}
      title={formatQuoteNumber(invNumber, "PC")}
      badge={t(`claims.clientStatus.${status}`)}
      status={status}
    >
      <Row className="main-row">
        <Col lg={6} xs={12} className="report-col">
          <DashboardCardField
            title={t("claims.date")}
            content={moment(claimDate).format("D MMMM YYYY")}
          />
        </Col>
      </Row>
      <Row className="main-row table-row">
        <CardTable rowCount table={claimTable} showCountTitle={true} />
      </Row>
      {variations && variations.length > 0 && (
        <Row className="main-row table-row">
          <CardTable
            rowCount
            table={claimVariationsTable}
            showCountTitle={true}
          />
        </Row>
      )}

      <Row className="row--big">
        <Col lg={7} xs={12} className="report-col--big">
          <DashboardCardField
            title={t("claims.comments")}
            placeholder={t("placeholders.noComments")}
            htmlContent={richComment?.html}
            height="180px"
          />
        </Col>
        <Col lg={5} xs={12} className="report-col--big">
          {renderTotal()}
        </Col>
      </Row>
      {hasReceipt && (
        <Row className="row--big">
          <Col lg={12} xs={12} className="report-col--big">
            <div className="">
              <div className="field-text field-text--underlined table-title">
                {t("orders.receipts")}
              </div>
              <CardTable
                rowCount
                table={receiptsData}
                showCountTitle={true}
                rowActions={receiptsRowActions}
              />
            </div>
          </Col>
        </Row>
      )}
    </ReportCard>
  );
};

export default ClientClaimCard;
